/**
 * THE write path (§6, §7 "Honest writes"). Every mutation in the product goes through here.
 *
 *  R1  every update/delete/insert asks for an exact row count;
 *  R2  a MISSING count means saved — only the literal 0 means "not saved";
 *  R3  a save is never detected by reading rows back.
 *
 * Under RLS a refused UPDATE/DELETE returns 200 with no error and 0 rows. `isNotSaved` is the one
 * predicate that catches it. A refusal is classified and named, never a generic failure.
 */
import type { PostgrestError, SupabaseClient } from '@supabase/supabase-js';
import { aliasFailure, noteFailure, type Failure } from './failures';

export const NOT_SAVED_TOAST_ID = 'write-not-saved';

export function isNotSaved({ error, count }: { error: unknown; count: number | null | undefined }): boolean {
  return !error && count === 0;
}

export function isPartialSave({ error, count }: { error: unknown; count: number | null | undefined }, expected: number): boolean {
  return !error && typeof count === 'number' && count > 0 && count < expected;
}

export type WriteFailure =
  | 'refused' // 0 rows: RLS said no, or the row is not visible to this principal
  | 'stale' // optimistic lock: someone else changed it first
  | 'signed-out'
  | 'forbidden' // authenticated, capability missing (42501 / trigger fence)
  | 'rejected' // a business rule the server enforces (capacity, lock, ceiling …) — message is user-facing
  | 'invalid' // constraint violation
  | 'conflict' // unique violation
  | 'offline'
  | 'network'
  | 'server';

export type WriteResult<T = unknown> =
  | { ok: true; count: number | null; partial?: { count: number; total: number }; data?: T }
  | { ok: false; reason: WriteFailure; message: string; error?: PostgrestError | Error; current?: Record<string, unknown> | null };

export const MESSAGES = {
  notSaved: "A change wasn't saved — the database didn't accept it, so it was undone.",
  partial: (count: number, total: number) => `Only ${count} of ${total} changes were saved — reload to see the current state.`,
  signedOut: "You're signed out. Sign in to save — your changes are still here.",
  offline: "You're offline — changes can't be saved until the connection returns.",
  network: 'Connection failed — check your internet and try again.',
  stale: 'Someone else changed this while you were editing. Review their change before saving yours.',
  server: "Couldn't save that. Please try again.",
  permission: "You don't have permission to do that.",
} as const;

/** Map a PostgREST/Postgres error to a named failure and user-facing copy (§7 refusal table). */
export function classifyError(error: PostgrestError | Error | null | undefined): { reason: WriteFailure; message: string } {
  if (!error) return { reason: 'server', message: MESSAGES.server };
  const e = error as PostgrestError & { status?: number };
  const code = e.code ?? '';
  const msg = e.message ?? '';
  if (/Failed to fetch|NetworkError|fetch failed|Load failed/i.test(msg)) return { reason: 'network', message: MESSAGES.network };
  if (/timed? ?out|AbortError/i.test(msg)) return { reason: 'network', message: 'The request timed out. Try again.' };
  if (code === 'PGRST301' || code === 'PGRST303' || /JWT expired|invalid JWT|not authenticated/i.test(msg) || e.status === 401) {
    return { reason: 'signed-out', message: MESSAGES.signedOut };
  }
  if (code === '42501') {
    // Our own trigger fences raise a sentence already written for people; the raw platform text is not.
    const human = /^[A-Z].*[.!]$/.test(msg) && !/permission denied|row-level security/i.test(msg);
    return { reason: 'forbidden', message: human ? msg : MESSAGES.permission };
  }
  if (code === 'P0001') return { reason: 'rejected', message: msg.endsWith('.') ? msg : `${msg}.` };
  // our routines raise invalid_parameter_value / no_data_found with a sentence written for people
  if (code === '22023' || code === 'P0002') {
    const human = /^[A-Z]/.test(msg);
    return { reason: code === 'P0002' ? 'refused' : 'invalid', message: human ? (/[.!]$/.test(msg) ? msg : `${msg}.`) : MESSAGES.server };
  }
  if (code === '23514' && msg.includes('_no_cvent')) return { reason: 'invalid', message: 'Guest messages can’t mention Cvent — reword and save again.' };
  if (code === '23514') return { reason: 'invalid', message: msg.includes('exceeds the organization ceiling') ? msg : "That value isn't allowed here — check the field and try again." };
  if (code === '23505') return { reason: 'conflict', message: 'That record already exists.' };
  if (code === '23503') return { reason: 'invalid', message: /^[A-Z].*\.$/.test(msg) ? msg : "This item is linked to other records and can't be removed." };
  if (code === '23502') return { reason: 'invalid', message: 'A required field is missing.' };
  if (code === '42703' || code === 'PGRST204') {
    // T30: name the column the database refused, so the fix is obvious
    const col = /'([^']+)' column/.exec(msg)?.[1] ?? /column "([^"]+)"/.exec(msg)?.[1];
    return { reason: 'server', message: col ? `The field “${col.replace(/_/g, ' ')}” can't be stored yet — nothing was saved.` : "This field can't be stored yet — nothing was saved." };
  }
  return { reason: 'server', message: MESSAGES.server };
}

function isOffline(): boolean {
  return typeof navigator !== 'undefined' && navigator.onLine === false;
}

type Failed = Extract<WriteResult, { ok: false }>;
/** Every failed write leaves here: it is noted (what failed, where) so the screen that shows it can offer Report. */
function failed(r: Omit<Failed, 'ok'>, apiPath: string, status?: number, stash = true): Failed {
  if (stash) {
    const e = r.error as (Partial<PostgrestError> & { status?: number }) | undefined;
    const apiStatus = status ?? e?.status;
    noteFailure({
      reason: r.reason, message: r.message, code: e?.code || undefined, details: e?.message || undefined, apiPath,
      apiStatus: typeof apiStatus === 'number' && apiStatus >= 100 && apiStatus <= 599 ? apiStatus : undefined,
    });
  }
  return { ok: false, ...r };
}
const OFFLINE = { reason: 'offline', message: MESSAGES.offline } as const;
const REFUSED = { reason: 'refused', message: MESSAGES.notSaved } as const;

type Filters = Record<string, string | number | boolean | null | readonly (string | number)[]>;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function applyFilters(q: any, filters: Filters) {
  for (const [k, v] of Object.entries(filters)) q = v === null ? q.is(k, null) : Array.isArray(v) ? q.in(k, v) : q.eq(k, v);
  return q;
}

/** Strip UI-only markers (`_x`) and blocked keys before a write (§6 allowlists). */
export function sanitizePatch(patch: Record<string, unknown>, blocked: readonly string[] = ['id', 'created_at', 'updated_at', 'version']) {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(patch)) if (!k.startsWith('_') && !blocked.includes(k) && v !== undefined) out[k] = v;
  return out;
}

export interface UpdateOptions {
  /** Rows the caller expects to change; enables the partial-save warning. */
  expected?: number;
  /** Optimistic lock (C5/C7): only write if the row is still at this version. */
  expectVersion?: number;
  /** Request headers the database may read (e.g. x-checkin-source for the audit row). */
  headers?: Record<string, string>;
}

export async function writeUpdate(
  client: SupabaseClient,
  table: string,
  patch: Record<string, unknown>,
  filters: Filters,
  opts: UpdateOptions = {},
): Promise<WriteResult> {
  const clean = sanitizePatch(patch);
  if (!Object.keys(clean).length) return { ok: true, count: 0 };
  if (isOffline()) return failed(OFFLINE, `/rest/v1/${table}`);
  let q = client.from(table).update(clean, { count: 'exact' });
  q = applyFilters(q, filters);
  if (opts.expectVersion !== undefined) q = q.eq('version', opts.expectVersion);
  for (const [k, v] of Object.entries(opts.headers ?? {})) q = q.setHeader(k, v);
  let res;
  try {
    res = await q;
  } catch (e) {
    return failed({ ...classifyError(e as Error), error: e as Error }, `/rest/v1/${table}`);
  }
  const { error, count, status } = res;
  if (error) return failed({ ...classifyError(error), error }, `/rest/v1/${table}`, status);
  if (isNotSaved({ error, count })) {
    if (opts.expectVersion !== undefined && 'id' in filters) {
      // Post-refusal DIAGNOSIS (never save detection): was it a concurrent edit, or a refusal?
      const cur = await client.from(table).select('*').eq('id', filters.id as string).maybeSingle();
      if (cur.data && (cur.data as { version?: number }).version !== opts.expectVersion) {
        return failed({ reason: 'stale', message: MESSAGES.stale, current: cur.data as Record<string, unknown> }, `/rest/v1/${table}`, status);
      }
    }
    return failed(REFUSED, `/rest/v1/${table}`, status);
  }
  if (opts.expected && isPartialSave({ error, count }, opts.expected)) {
    return { ok: true, count, partial: { count: count!, total: opts.expected } };
  }
  return { ok: true, count: count ?? null };
}

export async function writeInsert(client: SupabaseClient, table: string, rows: Record<string, unknown> | Record<string, unknown>[]): Promise<WriteResult> {
  const list = (Array.isArray(rows) ? rows : [rows]).map((r) => sanitizePatch(r, ['created_at', 'updated_at', 'version']));
  if (!list.length) return { ok: true, count: 0 };
  if (isOffline()) return failed(OFFLINE, `/rest/v1/${table}`);
  let res;
  try {
    // no .select(): R3. missing=default: a column a row omits gets its default, never NULL
    res = await client.from(table).insert(list, { count: 'exact', defaultToNull: false });
  } catch (e) {
    return failed({ ...classifyError(e as Error), error: e as Error }, `/rest/v1/${table}`);
  }
  const { error, count, status } = res;
  if (error) return failed({ ...classifyError(error), error }, `/rest/v1/${table}`, status);
  if (isNotSaved({ error, count })) return failed(REFUSED, `/rest/v1/${table}`, status);
  if (isPartialSave({ error, count }, list.length)) return { ok: true, count, partial: { count: count!, total: list.length } };
  return { ok: true, count: count ?? null };
}

export async function writeDelete(client: SupabaseClient, table: string, filters: Filters, opts: { expected?: number } = {}): Promise<WriteResult> {
  if (isOffline()) return failed(OFFLINE, `/rest/v1/${table}`);
  let res;
  try {
    res = await applyFilters(client.from(table).delete({ count: 'exact' }), filters);
  } catch (e) {
    return failed({ ...classifyError(e as Error), error: e as Error }, `/rest/v1/${table}`);
  }
  const { error, count, status } = res as { error: PostgrestError | null; count: number | null; status?: number };
  if (error) return failed({ ...classifyError(error), error }, `/rest/v1/${table}`, status);
  if (isNotSaved({ error, count })) return failed(REFUSED, `/rest/v1/${table}`, status);
  if (opts.expected && isPartialSave({ error, count }, opts.expected)) return { ok: true, count, partial: { count: count!, total: opts.expected } };
  return { ok: true, count: count ?? null };
}

/**
 * RPC writes: the function's own return value is the count/outcome; errors are classified the same way.
 * `noStash` keeps a failure out of the list Report reads from — the report call itself uses it.
 */
export async function writeRpc<T = unknown>(client: SupabaseClient, fn: string, args: Record<string, unknown>, opts?: { noStash?: boolean }): Promise<WriteResult<T>> {
  const path = `/rest/v1/rpc/${fn}`;
  const stash = !opts?.noStash;
  if (isOffline()) return failed(OFFLINE, path, undefined, stash);
  let res;
  try {
    res = await client.rpc(fn, args);
  } catch (e) {
    return failed({ ...classifyError(e as Error), error: e as Error }, path, undefined, stash);
  }
  if (res.error) return failed({ ...classifyError(res.error), error: res.error }, path, res.status, stash);
  return { ok: true, count: typeof res.data === 'number' ? res.data : null, data: res.data as T };
}

/**
 * A read or a dry run that failed and is shown in place as a sentence: what Report carries for it. A dropped
 * connection, being offline and a signed-out session give nothing — there is nothing to fix and no way to send it.
 */
export function loadFailure(message: string, error: unknown, apiPath?: string): Omit<Failure, 'at'> | null {
  if (isOffline()) return null;
  const e = (error ?? {}) as Partial<PostgrestError> & { status?: number };
  const reason = error ? classifyError(e as PostgrestError).reason : 'server';
  if (reason === 'network' || reason === 'offline' || reason === 'signed-out') return null;
  const status = e.status;
  return { reason: 'load', message, code: e.code || undefined, details: e.message || undefined, apiPath,
    apiStatus: typeof status === 'number' && status >= 100 && status <= 599 ? status : undefined };
}
/** The same, for a sentence a notification shows: noted, so the notification carries Report. */
export function noteLoadFailure(message: string, error: unknown, apiPath?: string): void {
  const f = loadFailure(message, error, apiPath);
  if (f) noteFailure(f);
}

/** "Not saved" for a routine that answered without an error and without doing anything: noted like any refused save. */
export function notSaved(apiPath?: string): string {
  noteFailure({ reason: 'refused', message: MESSAGES.notSaved, apiPath });
  return MESSAGES.notSaved;
}

/**
 * One stale-write sentence for every version-checked save: who, what changed, what to do. The refused save was noted
 * under the short sentence; it is found under this one too, so the notice that shows it carries Report (item F1).
 */
export function staleMessage(noun: string, theirs: Record<string, unknown> | null | undefined, loaded: Record<string, unknown>): string {
  const what = changedFields(theirs, loaded);
  const sentence = `Someone else changed this ${noun} while you were editing${what.length ? ` (${what.join(', ')})` : ''} — reload to see their change before saving yours.`;
  aliasFailure(MESSAGES.stale, sentence);
  return sentence;
}

const BOOKKEEPING = new Set(['version', 'updated_at', 'created_at', 'auto_tags', 'search_vector']);
/**
 * C5: the fields someone else changed since we loaded the row, as readable names — so a stale-write message
 * says what changed before anyone overwrites it.
 */
export function changedFields(theirs: Record<string, unknown> | null | undefined, loaded: Record<string, unknown>, labels: Record<string, string> = {}): string[] {
  if (!theirs) return [];
  return Object.keys(theirs)
    .filter((k) => !BOOKKEEPING.has(k) && k in loaded && !sameFieldValue(theirs[k] ?? null, loaded[k] ?? null))
    .map((k) => labels[k] ?? k.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase()));
}

/**
 * Per-field revert (§6): restore a field only while the local value is still the one we sent, so a newer
 * local edit made while the write was in flight survives.
 */
export function sameFieldValue(a: unknown, b: unknown): boolean {
  try {
    return Object.is(a, b) || JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return Object.is(a, b);
  }
}
export function revertFields<T extends Record<string, unknown>>(current: T, sent: Partial<T>, previous: Partial<T>): T {
  const next = { ...current };
  for (const k of Object.keys(sent) as (keyof T)[]) if (sameFieldValue(current[k], sent[k])) next[k] = previous[k] as T[keyof T];
  return next;
}
